import { AdminApiError, type AdminClient } from '@apx/admin-sdk';
import { showToast } from '../toast.js';
import { GAME_IDS, prettyGame } from '../games.js';

export interface MaintenancePageDeps {
  client: AdminClient;
}

const DEFAULT_MESSAGE = 'Game is temporarily under maintenance. Please check back shortly.';

/** Per-game maintenance surface value (subset we read). */
interface GameMaintenanceState {
  enabled?: boolean;
  message?: string;
  since?: string;
}

/**
 * Maintenance mode control. Two scopes:
 *  - GLOBAL: one Redis flag that stops every game for every integrator —
 *    the deploy/hotfix kill switch (use before a service-restarting deploy).
 *  - PER-GAME: pause one integrator+game (mirrors the abuse kill switch).
 *
 * Net effect when on: the scheduler stops opening new rounds, new bets are
 * rejected with GAME_UNDER_MAINTENANCE, and the game iframe shows the message.
 * In-flight rounds still settle normally.
 *
 * Both scopes show their CURRENT state (a pill + "since"), disable the action
 * that would be a no-op, and refresh after every change.
 */
export async function renderMaintenancePage(
  root: HTMLElement,
  deps: MaintenancePageDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Maintenance mode</h1>
    <p class="page-sub">
      Stop play cleanly before a deploy or hotfix. The scheduler stops opening new rounds,
      new bets are rejected, and players see a banner. In-flight rounds still settle.
    </p>

    <div class="card maint-card" style="margin-bottom:16px">
      <div class="maint-head">
        <div>
          <h2 style="margin:0">Global</h2>
          <span class="muted">All games, every integrator — the platform-wide deploy switch.</span>
        </div>
        <span id="maint-global-pill" class="pill">checking…</span>
      </div>
      <p class="muted" style="margin:10px 0 0">Engage it, wait for in-flight rounds to settle, then deploy. Lift it once health checks pass.</p>
      <div class="field" style="margin-top:14px">
        <label class="field-label" for="maint-global-msg">Player-facing message</label>
        <input id="maint-global-msg" class="field-input" type="text" maxlength="200" placeholder="${DEFAULT_MESSAGE}" />
      </div>
      <div class="maint-actions">
        <button id="maint-global-on" class="danger">Engage global maintenance</button>
        <button id="maint-global-off" class="primary">Lift global maintenance</button>
      </div>
    </div>

    <div class="card maint-card">
      <div class="maint-head">
        <div>
          <h2 style="margin:0">Per game + integrator</h2>
          <span class="muted">Pause one game for one integrator. Global overrides this when on.</span>
        </div>
        <span id="maint-game-pill" class="pill">select a game</span>
      </div>
      <div class="maint-grid" style="margin-top:14px">
        <div class="field" style="margin:0">
          <label class="field-label" for="maint-game-int">Integrator</label>
          <select id="maint-game-int" class="field-input"></select>
        </div>
        <div class="field" style="margin:0">
          <label class="field-label" for="maint-game-id">Game</label>
          <select id="maint-game-id" class="field-input"></select>
        </div>
      </div>
      <div class="field" style="margin-top:12px">
        <label class="field-label" for="maint-game-msg">Player-facing message</label>
        <input id="maint-game-msg" class="field-input" type="text" maxlength="200" placeholder="${DEFAULT_MESSAGE}" />
      </div>
      <div class="maint-actions">
        <button id="maint-game-on" class="danger">Pause game</button>
        <button id="maint-game-off" class="primary">Resume game</button>
      </div>
      <div id="maint-game-status" class="muted" style="margin-top:10px"></div>
    </div>
  `;

  const $ = <T extends HTMLElement>(sel: string): T => root.querySelector<T>(sel)!;

  // ── Global ────────────────────────────────────────────────────────────────
  const globalMsg = $<HTMLInputElement>('#maint-global-msg');
  const globalPill = $('#maint-global-pill');
  const globalOn = $<HTMLButtonElement>('#maint-global-on');
  const globalOff = $<HTMLButtonElement>('#maint-global-off');
  const globalCard = globalPill.closest('.maint-card') as HTMLElement;

  function paintGlobal(enabled: boolean, since?: string): void {
    globalPill.className = `pill ${enabled ? 'warn' : 'live'}`;
    globalPill.textContent = enabled
      ? `engaged${since ? ` · since ${fmtSince(since)}` : ''}`
      : 'running normally';
    globalCard.classList.toggle('engaged', enabled);
    globalOn.disabled = enabled;
    globalOff.disabled = !enabled;
  }

  async function refreshGlobal(): Promise<void> {
    try {
      const { maintenance } = await deps.client.getGlobalMaintenance();
      paintGlobal(maintenance.enabled, maintenance.since);
      if (maintenance.enabled && maintenance.message) globalMsg.value = maintenance.message;
    } catch (err) {
      globalPill.className = 'pill warn';
      globalPill.textContent = 'status unknown';
      showToast(describe(err), 'error');
    }
  }

  async function setGlobal(enabled: boolean): Promise<void> {
    globalOn.disabled = globalOff.disabled = true;
    try {
      await deps.client.setGlobalMaintenance(enabled, globalMsg.value || undefined);
      showToast(enabled ? 'Global maintenance engaged.' : 'Global maintenance lifted.', 'success');
      // Re-read for the authoritative `since` (the POST response omits it).
      await refreshGlobal();
    } catch (err) {
      showToast(describe(err), 'error');
      void refreshGlobal();
    }
  }

  globalOn.addEventListener('click', () => void setGlobal(true));
  globalOff.addEventListener('click', () => void setGlobal(false));

  // ── Per game ────────────────────────────────────────────────────────────────
  const intSelect = $<HTMLSelectElement>('#maint-game-int');
  const gameSelect = $<HTMLSelectElement>('#maint-game-id');
  const gameMsg = $<HTMLInputElement>('#maint-game-msg');
  const gamePill = $('#maint-game-pill');
  const gameOn = $<HTMLButtonElement>('#maint-game-on');
  const gameOff = $<HTMLButtonElement>('#maint-game-off');
  const gameStatus = $('#maint-game-status');

  function paintGame(state: GameMaintenanceState | null, loading = false): void {
    if (loading) {
      gamePill.className = 'pill';
      gamePill.textContent = 'checking…';
      gameOn.disabled = gameOff.disabled = true;
      return;
    }
    const paused = state?.enabled === true;
    gamePill.className = `pill ${paused ? 'warn' : 'live'}`;
    gamePill.textContent = paused
      ? `paused${state?.since ? ` · since ${fmtSince(state.since)}` : ''}`
      : 'active';
    (gamePill.closest('.maint-card') as HTMLElement).classList.toggle('engaged', paused);
    gameOn.disabled = paused;
    gameOff.disabled = !paused;
    if (paused && state?.message) gameMsg.value = state.message;
  }

  async function loadGames(integratorId: string): Promise<void> {
    try {
      const { games } = await deps.client.listGameCatalog(integratorId);
      gameSelect.innerHTML = games
        .map((g) => `<option value="${escapeHtml(g.gameId)}">${escapeHtml(prettyGame(g.gameId))}</option>`)
        .join('');
    } catch {
      // Catalog read failed — fall back to the platform catalog rather than a
      // hand-listed subset, which silently hid every game added since.
      gameSelect.innerHTML = GAME_IDS.map(
        (id) => `<option value="${id}">${escapeHtml(prettyGame(id))}</option>`,
      ).join('');
    }
  }

  async function refreshGame(): Promise<void> {
    const integratorId = intSelect.value;
    const gameId = gameSelect.value;
    if (!integratorId || !gameId) return;
    paintGame(null, true);
    try {
      const { value } = await deps.client.getSurface(integratorId, gameId, 'maintenance');
      paintGame((value as GameMaintenanceState | null) ?? null);
    } catch (err) {
      gamePill.className = 'pill';
      gamePill.textContent = 'status unknown';
      gameOn.disabled = gameOff.disabled = false; // allow action even if read failed
      gameStatus.innerHTML = `<span class="error">Could not read current state: ${escapeHtml(describe(err))}</span>`;
    }
  }

  async function setGame(enabled: boolean): Promise<void> {
    const integratorId = intSelect.value;
    const gameId = gameSelect.value;
    if (!integratorId || !gameId) {
      showToast('Pick an integrator and game.', 'error');
      return;
    }
    gameOn.disabled = gameOff.disabled = true;
    gameStatus.textContent = '';
    try {
      await deps.client.setGameMaintenance(integratorId, gameId, enabled, gameMsg.value || undefined);
      showToast(
        enabled ? `Paused ${prettyGame(gameId)} for this integrator.` : `Resumed ${prettyGame(gameId)}.`,
        'success',
      );
      await refreshGame();
    } catch (err) {
      showToast(describe(err), 'error');
      void refreshGame();
    }
  }

  gameOn.addEventListener('click', () => void setGame(true));
  gameOff.addEventListener('click', () => void setGame(false));
  intSelect.addEventListener('change', async () => {
    await loadGames(intSelect.value);
    await refreshGame();
  });
  gameSelect.addEventListener('change', () => void refreshGame());

  // ── Init ─────────────────────────────────────────────────────────────────
  try {
    const integrators = await deps.client.listIntegrators();
    intSelect.innerHTML = integrators
      .map((i) => `<option value="${i.id}">${escapeHtml(i.name ?? i.id)}</option>`)
      .join('');
    if (integrators[0]) {
      await loadGames(integrators[0].id);
    }
  } catch (err) {
    gameStatus.innerHTML = `<span class="error">Could not load integrators: ${escapeHtml(describe(err))}</span>`;
  }

  await Promise.all([refreshGlobal(), refreshGame()]);
}

/** Compact "since" — short local date-time, falls back to the raw value. */
function fmtSince(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}
